// The end of a run, as a pure model.
//
// The mirror of `celebrations.ts`, and split the same way: this is the
// question of *what happened*, `RunOver.tsx` is the pixels. Losing used to be
// completely silent — the save was wiped and the status chip turned red, so
// the only way to find out the run was over was to notice that nothing worked
// any more and that Continue had stopped being offered.
//
// One predicate, in one place. `App.tsx` reads it to decide whether to keep
// the save and the shell reads it to decide whether to say so, and those two
// answers have to be the same one: a run the app has quietly thrown away while
// the board still looks playable is the worst version of this.
//
// Named for the ending rather than for the overlay, because `RunOver.tsx` is
// the overlay and a case-insensitive filesystem cannot hold both.

import type { GameState } from "src/engine/rules/commands";
import { livingSides } from "src/engine/rules/position";
import { stagesCleared } from "src/engine/ai/waves";
import { addTally, EMPTY_TALLY } from "src/engine/rules/tally";
import { runIsWon } from "src/engine/rules/campaign/objectives";
import { type Stat, statsOf } from "src/engine/session/celebrations";

/**
 * How a run ended.
 *
 * The one thing the copy changes for. Being mated, being left without a legal
 * move and having your army decide there is nothing left to play for are the
 * same result and three completely different stories, and a card that told
 * none of them would be a card nobody reads twice.
 */
export type DefeatReason = "mated" | "stalemated" | "despaired";

export interface Defeat {
  reason: DefeatReason;
  /** How far the run got, which is what "how did I do" means in a roguelike. */
  stage: number;
  /** The front you were standing on when it ended. */
  stats: Stat[];
}

/**
 * How a run finished, whichever way it went.
 *
 * **One model for both endings, because they are one moment.** A run that ends
 * has to say so, offer the same two ways out, and report the same four numbers
 * — what differs is the palette and the sentences. Two models would be two
 * places to remember that a finished run must not still be `Continue`-able.
 *
 * `reason` is null on a win: there is only one way to win, so there is nothing
 * for the copy to key off.
 */
export interface RunEnding {
  won: boolean;
  reason: DefeatReason | null;
  stage: number;
  stats: Stat[];
}

/**
 * Has the player's own army been beaten?
 *
 * Only mate and stalemate reach here: a run does not end on any of the three
 * chess draws, which is a rule of its own — an industrial phase *is* fifty
 * quiet moves. And it has to be the **run's** side rather than whoever is on
 * the clock: a bifurcation gives you an allied army, and that army being beaten is
 * a loss of half your pieces rather than the end of the run.
 */
export function runIsLost(state: GameState): boolean {
  return defeatReason(state) !== null;
}

/**
 * Why the run is over, or null while it is alive.
 *
 * **The army being gone is its own ending, and it used to be no ending at
 * all.** Despair sweeps a side into scrap through `surrender`, which leaves
 * the clock on somebody who still exists — so the status is `playing`, nothing
 * is mated, and a player whose whole army had just laid down its arms was left
 * on a board with nothing on it and no way to say so. Checked first, because
 * a side with no pieces cannot be mated and the other test would never fire.
 */
function defeatReason(state: GameState): DefeatReason | null {
  const run = state.run;
  if (!run) return null;

  if (!livingSides(state.position).includes(run.side)) return "despaired";
  if (state.position.turn !== run.side) return null;
  if (state.status === "checkmate") return "mated";
  if (state.status === "stalemate") return "stalemated";
  return null;
}

/**
 * The card to show, or null while the run is still alive.
 *
 * The stage comes off the state, which is the same source the stage chip in
 * the header reads — a card claiming stage four beside a chip saying three is
 * worse than a card with no number on it at all. It used to come off the
 * *log*, and a reloaded run then lost the round: the save is a snapshot, so
 * both readings answered stage one however deep the run was.
 */
export function defeatOf(state: GameState): Defeat | null {
  const reason = defeatReason(state);
  if (!reason) return null;
  return {
    reason,
    stage: stagesCleared(state) + 1,
    // The stage in progress, not the one before it: this is the front the run
    // died on, and `lastStage` is the one it survived.
    stats: statsOf(state.tally ?? EMPTY_TALLY)
  };
}

/**
 * The whole run's numbers: every front it finished, plus the one it was on.
 *
 * **The run-over card is about the run**, and it used to read `tally` — which
 * is the front in progress. A run that had cleared three fronts reported what
 * had happened since the last boundary, so losing a turn after clearing one
 * said you had made two moves and dug nothing. The round card is the one that
 * is about a single front, and it keeps reading `lastStage`.
 */
const runStats = (state: GameState): Stat[] =>
  statsOf(addTally(state.runTally ?? EMPTY_TALLY, state.tally ?? EMPTY_TALLY));

/**
 * Is the run over, either way?
 *
 * What `App.tsx` asks before keeping the save. A won run must be thrown away
 * as surely as a lost one: offering Continue into a board whose last objective
 * is already done is a run you cannot play and cannot finish.
 */
export const runIsOver = (state: GameState): boolean =>
  runIsLost(state) || runIsWon(state);

/**
 * The plate to show, or null while the run is still alive.
 *
 * Winning is checked first. The launch is the last objective completing, and
 * the front you were standing on when you did it is very likely a beaten army
 * — so the same state can satisfy both tests, and "you won" is the true answer
 * to it.
 */
export function endingOf(state: GameState): RunEnding | null {
  if (runIsWon(state)) {
    return { won: true, reason: null, stage: stagesCleared(state) + 1, stats: runStats(state) };
  }
  const lost = defeatOf(state);
  return lost ? { won: false, ...lost, stats: runStats(state) } : null;
}
